// ** React Imports
import React, { Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'

// ** Redux Imports
import { Provider } from 'react-redux'
import { store } from './redux/storeConfig/store'

// ** Intl, CASL & ThemeColors Context
import ability from '@configs/acl/ability'
import { ToastContainer } from 'react-toastify'
import { AbilityContext } from '@src/utility/context/Can'
import { ThemeContext } from '@src/utility/context/ThemeColors'
import { IntlProviderWrapper } from '@src/utility/context/Internationalization'
import { GlobalDefinitionsProviderWrapper } from "@csrc/utility/context/GlobalDefinitions"
import { registerSyncFusionLicense, env } from "@utils"

// ** Spinner (Splash Screen)
import Spinner from '@components/spinner/Fallback-spinner'

// ** Ripple Button
// import '@components/ripple-button'

// ** PrismJS
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/components/prism-jsx.min'

// ** React Perfect Scrollbar
import 'react-perfect-scrollbar/dist/css/styles.css'

// ** React Toastify
import '@styles/react/libs/toastify/toastify.scss'

// ** Core styles
import '@assets/fonts/feather/iconfont.css'
import '@styles/core.scss'
import '@src/assets/scss/style.scss'
import './assets/scss/style.scss'
import '@src/assets/scss/bootstrap-5-migrate.scss'

// ** Service Worker
import * as serviceWorker from './serviceWorker'

// ** Langs
import { genLang } from "./assets/data/locales"
import { dashboardLang } from "@cmodules/dashboard"
import { checkListLang } from "@cmodules/checklist"
import { resourcesLang } from "@cmodules/resources"
import { assetLang } from "@cmodules/asset"
import { issuesLang } from "@cmodules/issues"
import { assignmentLang } from "@cmodules/assignment"
import { inspectionsLang } from "@cmodules/inspections"
import { analyticsLang } from "@cmodules/analytics"
import { settingsLang } from "@cmodules/settings"

// Sentry initialization should be imported first!
import "./instrument"

// // React 19 compat: restore defaultProps behavior for function components.
// // Required for reactstrap v8 and other CJS libraries that depend on this pattern.
// // We patch both React.createElement (used by CJS libraries like reactstrap internally)
// // AND react/jsx-runtime + react/jsx-dev-runtime (used by project source files compiled
// // with Babel's automatic JSX transform, where <Progress .../> becomes _jsx(Progress, ...)).
// ; (function () {
//   function applyDefaults(type, props) {
//     if (typeof type === 'function' && !type.prototype?.isReactComponent && type.defaultProps) {
//       const defs = type.defaultProps
//       props = Object.assign({}, defs, props || undefined)
//       for (const key in defs) {
//         if (props[key] === undefined) props[key] = defs[key]
//       }
//     }
//     return props
//   }

//   // Patch React.createElement (CJS libraries use _react.default.createElement at call-time)
//   const _origCreate = React.createElement
//   React.createElement = function (type, props, ...args) {
//     return _origCreate(type, applyDefaults(type, props), ...args)
//   }

//   // Patch react/jsx-runtime (automatic JSX transform in project source files).
//   // webpack compiles `import { jsx as _jsx } from 'react/jsx-runtime'` to a property
//   // access on the module exports object at call-time, so mutating .jsx/.jsxs is visible.
//   try {
//     const jsr = require('react/jsx-runtime') // eslint-disable-line
//     const origJsx = jsr.jsx
//     const origJsxs = jsr.jsxs
//     jsr.jsx = function (type, props, key) { return origJsx(type, applyDefaults(type, props), key) }
//     jsr.jsxs = function (type, props, key) { return origJsxs(type, applyDefaults(type, props), key) }
//   } catch (e) { /* noop — module may not exist in all environments */ }

//   // Patch react/jsx-dev-runtime (development mode automatic JSX transform)
//   try {
//     const jsdr = require('react/jsx-dev-runtime') // eslint-disable-line
//     const origJsxDEV = jsdr.jsxDEV
//     jsdr.jsxDEV = function (type, props, key, isStatic, source, self) {
//       return origJsxDEV(type, applyDefaults(type, props), key, isStatic, source, self)
//     }
//   } catch (e) { /* noop */ }
// })()

// ** Lazy load app
const LazyApp = lazy(() => import('./App'))

const additionalLangs = {
  ar: { ...genLang.ar, ...dashboardLang.ar, ...checkListLang.ar, ...resourcesLang.ar, ...assetLang.ar, ...issuesLang.ar, ...assignmentLang.ar, ...inspectionsLang.ar, ...analyticsLang.ar, ...settingsLang.ar },
  cs: { ...genLang.cs, ...dashboardLang.cs, ...checkListLang.cs, ...resourcesLang.cs, ...assetLang.cs, ...issuesLang.cs, ...assignmentLang.cs, ...inspectionsLang.cs, ...analyticsLang.cs, ...settingsLang.cs },
  de: { ...genLang.de, ...dashboardLang.de, ...checkListLang.de, ...resourcesLang.de, ...assetLang.de, ...issuesLang.de, ...assignmentLang.de, ...inspectionsLang.de, ...analyticsLang.de, ...settingsLang.de },
  en: { ...genLang.en, ...dashboardLang.en, ...checkListLang.en, ...resourcesLang.en, ...assetLang.en, ...issuesLang.en, ...assignmentLang.en, ...inspectionsLang.en, ...analyticsLang.en, ...settingsLang.en },
  es: { ...genLang.es, ...dashboardLang.es, ...checkListLang.es, ...resourcesLang.es, ...assetLang.es, ...issuesLang.es, ...assignmentLang.es, ...inspectionsLang.es, ...analyticsLang.es, ...settingsLang.es },
  fr: { ...genLang.fr, ...dashboardLang.fr, ...checkListLang.fr, ...resourcesLang.fr, ...assetLang.fr, ...issuesLang.fr, ...assignmentLang.fr, ...inspectionsLang.fr, ...analyticsLang.fr, ...settingsLang.fr },
  hi: { ...genLang.hi, ...dashboardLang.hi, ...checkListLang.hi, ...resourcesLang.hi, ...assetLang.hi, ...issuesLang.hi, ...assignmentLang.hi, ...inspectionsLang.hi, ...analyticsLang.hi, ...settingsLang.hi },
  id: { ...genLang.id, ...dashboardLang.id, ...checkListLang.id, ...resourcesLang.id, ...assetLang.id, ...issuesLang.id, ...assignmentLang.id, ...inspectionsLang.id, ...analyticsLang.id, ...settingsLang.id },
  it: { ...genLang.it, ...dashboardLang.it, ...checkListLang.it, ...resourcesLang.it, ...assetLang.it, ...issuesLang.it, ...assignmentLang.it, ...inspectionsLang.it, ...analyticsLang.it, ...settingsLang.it },
  nl: { ...genLang.nl, ...dashboardLang.nl, ...checkListLang.nl, ...resourcesLang.nl, ...assetLang.nl, ...issuesLang.nl, ...assignmentLang.nl, ...inspectionsLang.nl, ...analyticsLang.nl, ...settingsLang.nl },
  pl: { ...genLang.pl, ...dashboardLang.pl, ...checkListLang.pl, ...resourcesLang.pl, ...assetLang.pl, ...issuesLang.pl, ...assignmentLang.pl, ...inspectionsLang.pl, ...analyticsLang.pl, ...settingsLang.pl },
  pt: { ...genLang.pt, ...dashboardLang.pt, ...checkListLang.pt, ...resourcesLang.pt, ...assetLang.pt, ...issuesLang.pt, ...assignmentLang.pt, ...inspectionsLang.pt, ...analyticsLang.pt, ...settingsLang.pt },
  ru: { ...genLang.ru, ...dashboardLang.ru, ...checkListLang.ru, ...resourcesLang.ru, ...assetLang.ru, ...issuesLang.ru, ...assignmentLang.ru, ...inspectionsLang.ru, ...analyticsLang.ru, ...settingsLang.ru },
  sk: { ...genLang.sk, ...dashboardLang.sk, ...checkListLang.sk, ...resourcesLang.sk, ...assetLang.sk, ...issuesLang.sk, ...assignmentLang.sk, ...inspectionsLang.sk, ...analyticsLang.sk, ...settingsLang.sk },
  tr: { ...genLang.tr, ...dashboardLang.tr, ...checkListLang.tr, ...resourcesLang.tr, ...assetLang.tr, ...issuesLang.tr, ...assignmentLang.tr, ...inspectionsLang.tr, ...analyticsLang.tr, ...settingsLang.tr },
  ur: { ...genLang.ur, ...dashboardLang.ur, ...checkListLang.ur, ...resourcesLang.ur, ...assetLang.ur, ...issuesLang.ur, ...assignmentLang.ur, ...inspectionsLang.ur, ...analyticsLang.ur, ...settingsLang.ur }
}
//************************************//
registerSyncFusionLicense()
const root = createRoot(document.getElementById('root'), {
  onCaughtError(error, errorInfo) {
    console.error('React caught error:', error, errorInfo.componentStack)
  },
  onUncaughtError(error, errorInfo) {
    console.error('React uncaught error:', error, errorInfo.componentStack)
  },
  onRecoverableError(error, errorInfo) {
    console.error('React recoverable error:', error, errorInfo.componentStack)
  }
})
root.render(
  <Provider store={store}>
    <Suspense fallback={<Spinner />}>
      <AbilityContext.Provider value={ability}>
        <ThemeContext>
          <IntlProviderWrapper additionalLangs={additionalLangs}>
            <GlobalDefinitionsProviderWrapper>
              {/* <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}> */}
              <LazyApp />
              <ToastContainer newestOnTop />
              {/* </Sentry.ErrorBoundary> */}
            </GlobalDefinitionsProviderWrapper>
          </IntlProviderWrapper>
        </ThemeContext>
      </AbilityContext.Provider>
    </Suspense>
  </Provider>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
